import { isOnBlogSubdomain, shouldIntercept } from "@/lib/blog-host";
import { cn } from "@/lib/utils";
import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Interstitial that intercepts navigation BETWEEN the main Corgi Funds site
 * (corgifunds.com) and the blog (blog.corgifunds.com) — treated as separate
 * sites for legal/disclosure purposes. Direction-aware copy: "Leaving" when
 * going main → blog, "Entering" when going blog → main.
 *
 * Ported from dane/frontend/app/etf-website/components/leaving-site-modal.tsx,
 * adapted to apps/web (react-router, root-level routes, the `@/lib/blog-host`
 * host helpers). Mounted once in <Layout> so it listens on whichever host
 * serves the SPA. Third-party links are NOT intercepted (see `shouldIntercept`).
 */

const LEAVING_TITLE = "You’re Leaving Corgi Funds";
const LEAVING_DESC =
  "You are about to navigate to our blog, which is hosted on a separate platform. Corgi Funds is not responsible for the content or privacy practices of third-party sites.";

const ENTERING_TITLE = "You’re Entering Corgi Funds";
const ENTERING_DESC =
  "You are about to return to the main Corgi Funds website. The views expressed on the blog do not necessarily reflect those of Corgi Funds or its affiliates.";

export function LeavingSiteModal() {
  const [isOpen, setIsOpen] = useState(false);
  const [targetHref, setTargetHref] = useState("");
  const goBackRef = useRef<HTMLButtonElement>(null);

  const close = useCallback(() => {
    setIsOpen(false);
    setTargetHref("");
    document.body.style.overflow = "";
  }, []);

  // Continue MUST keep `targetHref` intact across close — clearing it
  // synchronously can race with React 18's batched re-render and clobber the
  // anchor's href *before* the browser fires the default navigation, which is
  // exactly what makes "Continue" feel broken. Just hide + restore scroll;
  // `targetHref` is overwritten on the next interception anyway.
  const closeForContinue = useCallback(() => {
    setIsOpen(false);
    document.body.style.overflow = "";
  }, []);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      const anchor = (e.target as HTMLElement).closest?.("a[href]") as HTMLAnchorElement | null;
      if (!anchor) return;

      // The modal's own Continue link IS a cross-host link; let it through so
      // this capture-phase listener doesn't re-open the modal in a loop.
      if (anchor.dataset.leaveSiteBypass !== undefined) return;

      // Let the browser handle open-in-new-tab / new-window / download and any
      // non-primary-button click normally — don't trap modified clicks.
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;

      const href = anchor.getAttribute("href") ?? "";
      if (
        !href ||
        href.startsWith("#") ||
        href.startsWith("mailto:") ||
        href.startsWith("tel:") ||
        href.startsWith("javascript:")
      ) {
        return;
      }

      let url: URL;
      try {
        url = new URL(href, window.location.origin);
      } catch {
        return;
      }

      if (!shouldIntercept(window.location.hostname, url.hostname)) return;

      // preventDefault (not stopPropagation): block the navigation but let the
      // click keep bubbling so the source element's own onClick still runs —
      // e.g. the mobile nav links close the menu via their onClick handler.
      e.preventDefault();
      setTargetHref(url.toString());
      setIsOpen(true);
      document.body.style.overflow = "hidden";
    }

    // Capture phase so this fires before react-router's own bubble-phase
    // <Link> handler and before any anchor onClick.
    document.addEventListener("click", handleClick, true);
    return () => document.removeEventListener("click", handleClick, true);
  }, []);

  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape" && isOpen) close();
    }
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [isOpen, close]);

  // Move focus into the dialog when it opens — land on the safe "Go Back"
  // default rather than the destructive Continue action.
  useEffect(() => {
    if (isOpen) goBackRef.current?.focus();
  }, [isOpen]);

  const entering = isOnBlogSubdomain();
  const title = entering ? ENTERING_TITLE : LEAVING_TITLE;
  const description = entering ? ENTERING_DESC : LEAVING_DESC;

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: backdrop click-to-dismiss; keyboard (Escape) dismissal is handled by the global keydown listener above
    <div
      className={cn(
        "fixed inset-0 z-[10000] bg-black/65 backdrop-blur-[6px] flex items-center justify-center opacity-0 invisible transition-[opacity,visibility] duration-[250ms] ease-in-out",
        isOpen && "opacity-100 visible",
      )}
      onClick={(e) => {
        if (e.target === e.currentTarget) close();
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="leaving-site-title"
      aria-describedby="leaving-site-desc"
      aria-hidden={!isOpen}
    >
      <div
        className={cn(
          "bg-[var(--etf-black2)] border border-[var(--etf-border)] rounded-xl max-w-[420px] w-[calc(100%-48px)] px-9 pt-10 pb-8 text-center transition-transform duration-300",
          isOpen ? "scale-100 translate-y-0" : "scale-[0.92] translate-y-2",
        )}
        style={{ transitionTimingFunction: "cubic-bezier(0.16, 1, 0.3, 1)" }}
      >
        <div className="w-12 h-12 mx-auto mb-5 rounded-full bg-[rgba(255,85,0,0.1)] border border-[rgba(255,85,0,0.2)] flex items-center justify-center">
          <svg
            aria-hidden="true"
            className="w-[22px] h-[22px] stroke-[var(--etf-orange)] fill-none"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            viewBox="0 0 24 24"
          >
            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
            <polyline points="15 3 21 3 21 9" />
            <line x1="10" y1="14" x2="21" y2="3" />
          </svg>
        </div>
        <div
          id="leaving-site-title"
          className="font-[family-name:var(--font-f37bolton)] text-xl font-semibold text-[var(--etf-white)] mb-2.5 tracking-[-0.01em]"
        >
          {title}
        </div>
        <div
          id="leaving-site-desc"
          className="text-sm text-[var(--etf-white3)] leading-[1.55] mb-7"
        >
          {description}
        </div>
        <div className="flex gap-2.5">
          <button
            ref={goBackRef}
            type="button"
            className="flex-1 py-3 px-5 rounded-lg font-[family-name:var(--font-geist)] text-sm font-medium cursor-pointer transition-all duration-150 bg-transparent border border-[var(--etf-border)] text-[var(--etf-white2)] hover:border-[var(--etf-white3)] hover:text-[var(--etf-white)] inline-flex items-center justify-center"
            onClick={close}
          >
            Go Back
          </button>
          <a
            className="flex-1 py-3 px-5 rounded-lg font-[family-name:var(--font-geist)] text-sm font-medium cursor-pointer transition-all duration-150 bg-[var(--etf-orange)] border border-[var(--etf-orange)] text-white no-underline hover:brightness-110 inline-flex items-center justify-center"
            href={targetHref}
            data-leave-site-bypass=""
            onClick={closeForContinue}
          >
            Continue
            <svg
              aria-hidden="true"
              className="w-3.5 h-3.5 ml-1.5 stroke-current fill-none"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              viewBox="0 0 24 24"
            >
              <line x1="5" y1="12" x2="19" y2="12" />
              <polyline points="12 5 19 12 12 19" />
            </svg>
          </a>
        </div>
      </div>
    </div>
  );
}
